/**
 * Meta Pixel helper — country-aware.
 *
 * Pixel IDs are managed per country in COUNTRY_CONFIG (src/lib/country.js),
 * sourced from env vars (REACT_APP_META_PIXEL_ID_NZ / _AU). NO Pixel ID is
 * hardcoded for AU — until an AU Pixel ID is configured, `initPixel` is a
 * no-op and the AU instance never loads Facebook's script.
 *
 * `initPixel(id)` is called once from <MetaPixelInit> (App.js) with the
 * active country's Pixel ID. All tracking helpers no-op safely when the
 * Pixel is not loaded (`window.fbq` undefined), so calling them from any
 * component is always safe.
 *
 * Usage:
 *   import { pixelTrack, pixelPageView } from '../lib/metaPixel';
 *   pixelTrack('Lead', { content_name: 'Provider Signup' });
 */

const STANDARD_EVENTS = new Set([
  'PageView', 'ViewContent', 'Lead', 'CompleteRegistration', 'InitiateCheckout',
  'Subscribe', 'Purchase', 'AddToCart', 'AddPaymentInfo', 'Contact',
  'StartTrial', 'Search',
]);

const isEnabled = () =>
  typeof window !== 'undefined' && typeof window.fbq === 'function';

/**
 * Initialise the Meta Pixel for the active country. Safe to call multiple
 * times — only the first call with a non-empty pixelId takes effect.
 * Fires the initial PageView once. No-op when pixelId is empty (e.g. AU
 * before its Pixel ID is configured).
 */
export const initPixel = (pixelId) => {
  if (typeof window === 'undefined') return;
  if (!pixelId) return;                     // AU not configured yet → no init
  if (window.__pixelInitDone) return;       // guard against double-init (HMR / remount)
  if (window.fbq && window.fbq.loaded) return;

  try {
    /* eslint-disable */
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */
    window.fbq('init', pixelId);
    window.fbq('track', 'PageView');        // single initial PageView
    window.__pixelInitDone = true;
    window.__metaPixelEnabled = true;
  } catch (e) {
    /* fail silent — never block page render */
  }
};

/** Fire a Meta Pixel standard or custom event safely. No-op if Pixel not loaded. */
export const pixelTrack = (eventName, params = {}) => {
  if (!isEnabled()) return;
  try {
    if (STANDARD_EVENTS.has(eventName)) {
      window.fbq('track', eventName, params);
    } else {
      window.fbq('trackCustom', eventName, params);
    }
  } catch (e) {
    /* never let pixel errors break the app */
  }
};

/**
 * Fire an event at most once per session, keyed by `dedupeKey`. Useful for
 * events like ViewContent (per item) or Subscribe (once) that could re-fire
 * on remount / re-render.
 */
export const pixelTrackOnce = (dedupeKey, eventName, params = {}) => {
  if (typeof window === 'undefined') return;
  if (!window.__pixelFiredOnce) window.__pixelFiredOnce = {};
  if (window.__pixelFiredOnce[dedupeKey]) return;
  window.__pixelFiredOnce[dedupeKey] = true;
  pixelTrack(eventName, params);
};

/** Fire a PageView for SPA route changes. */
export const pixelPageView = () => pixelTrack('PageView');
